import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Autocomplete
} from '@mui/material';
import { Transaction } from '../../types';
import { useBudget } from '../../contexts/BudgetContext';

interface EditTransactionDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (transactionId: string, updatedTransaction: Partial<Transaction>) => void;
  transaction: Transaction | null;
}

const EditTransactionDialog: React.FC<EditTransactionDialogProps> = ({ 
  open, 
  onClose, 
  onSubmit, 
  transaction 
}) => {
  const { currentBudget } = useBudget();
  const [formData, setFormData] = useState({
    date: '',
    description: '',
    amount: '',
    type: 'expense' as 'income' | 'expense' | 'savings',
    category: '',
    notes: ''
  });

  // Filter categories based on transaction type
  const availableCategories = useMemo(() => {
    if (!currentBudget) return [];
    return currentBudget.categories
      .filter(cat => cat.type === formData.type)
      .map(cat => cat.name);
  }, [currentBudget, formData.type]);

  useEffect(() => {
    if (transaction) {
      setFormData({
        date: transaction.date.split('T')[0],
        description: transaction.description,
        amount: transaction.amount.toString(),
        type: transaction.type,
        category: transaction.category,
        notes: transaction.notes || ''
      });
    }
  }, [transaction]);

  const handleSubmit = () => {
    if (!transaction || !formData.description || !formData.amount || !formData.category) return;

    onSubmit(transaction._id, {
      date: formData.date,
      description: formData.description,
      amount: Number(formData.amount),
      type: formData.type,
      category: formData.category,
      notes: formData.notes
    });
  };

  const handleTypeChange = (type: 'income' | 'expense' | 'savings') => {
    setFormData(prev => ({
      ...prev,
      type,
      category: '' // Reset category when type changes
    }));
  };

  if (!transaction) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Transaction</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <TextField
            label="Date"
            type="date"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            required
            InputLabelProps={{
              shrink: true
            }}
          />
          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            required
          />
          <TextField
            label="Amount"
            type="number"
            value={formData.amount}
            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
            required
          />
          <TextField
            select
            label="Type"
            value={formData.type}
            onChange={(e) => handleTypeChange(e.target.value as 'income' | 'expense' | 'savings')}
            required
          >
            <MenuItem value="income">Income</MenuItem>
            <MenuItem value="expense">Expense</MenuItem>
            <MenuItem value="savings">Savings</MenuItem>
          </TextField>
          <Autocomplete
            value={formData.category}
            onChange={(_, newValue) => setFormData({ ...formData, category: newValue || '' })}
            options={availableCategories}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Category"
                required
                error={!formData.category}
                helperText={!formData.category ? 'Please select a category' : ''}
              />
            )}
            freeSolo
            autoComplete
            autoHighlight
            clearOnBlur
            handleHomeEndKeys
          />
          <TextField
            label="Notes"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            multiline
            rows={3}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          disabled={!formData.description || !formData.amount || !formData.category}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditTransactionDialog; 